import {
  formatSandboxDimensions,
  formatSandboxWeight,
  type SandboxDimensionUnit,
} from "@shared/sandboxProjectCatalogue";
import {
  createDefaultProjectMarkupLayoutDocument,
  parseProjectMarkupLayoutDocument,
  type ProjectMarkupCanvasSize,
  type ProjectMarkupLayoutDocument,
  type ProjectMarkupPlanInformation,
  type ProjectMarkupPlanTransform,
  type ProjectMarkupWorkspaceView,
} from "@shared/projectMarkupLayoutDocument";
import { formatProductBrandModelReference, resolveProductDisplayName } from "@shared/productDisplayName";
import type { RouterOutputs } from "@/lib/trpc";

export type MarkupShape = "circle" | "square" | "triangle";

export type MarkupColor = "red" | "blue" | "green" | "yellow" | "purple" | "black";

export type ProjectMarkupComponent = {
  id: string;
  catalogueId: number;
  name: string;
  /** Technical reference shown under display name in callouts. */
  subtitle?: string | null;
  category: string;
  qty: number;
  imageUrl: string | null;
  dimensionsLabel: string | null;
  weightLabel: string | null;
};

export type PlacedMarker = {
  id: string;
  componentId: string;
  pageNumber: number;
  /** Drawing-space: % of floor-plan width (0–100). */
  x: number;
  /** Drawing-space: % of floor-plan height (0–100). */
  y: number;
  shape: MarkupShape;
  color: MarkupColor;
  /** Canvas-space: % of presentation canvas width. */
  calloutX: number;
  /** Canvas-space: % of presentation canvas height. */
  calloutY: number;
  markerScale: number;
  calloutWidth: number;
  /** Preferred / minimum callout height; cards may grow taller to fit content. */
  calloutHeight: number;
  createdAt: string;
};

export type MarkupLayoutDocument = ProjectMarkupLayoutDocument<PlacedMarker>;
export type MarkupWorkspaceView = ProjectMarkupWorkspaceView;
export type MarkupPlanTransform = ProjectMarkupPlanTransform;
export type MarkupCanvasSize = ProjectMarkupCanvasSize;
export type MarkupPlanInformation = ProjectMarkupPlanInformation;

export function parseMarkupLayoutDocument(raw: unknown): MarkupLayoutDocument {
  return parseProjectMarkupLayoutDocument<PlacedMarker>(raw);
}

export function createDefaultMarkupLayoutDocument(markers: PlacedMarker[] = []): MarkupLayoutDocument {
  return createDefaultProjectMarkupLayoutDocument<PlacedMarker>(markers);
}

export const DEFAULT_MARKER_SCALE = 1;
/** ~18% smaller than the original 132×76, but wide enough for wrapped titles. */
export const DEFAULT_CALLOUT_WIDTH = 120;
export const DEFAULT_CALLOUT_HEIGHT = 72;
export const BASE_MARKER_SIZE = 14;
export const MIN_MARKER_SCALE = 0.5;
export const MAX_MARKER_SCALE = 3;
export const MIN_CALLOUT_WIDTH = 100;
export const MIN_CALLOUT_HEIGHT = 56;

export const MARKUP_SHAPES: { id: MarkupShape; label: string }[] = [
  { id: "circle", label: "Circle" },
  { id: "square", label: "Square" },
  { id: "triangle", label: "Triangle" },
];

export const MARKUP_COLORS: { id: MarkupColor; hex: string; label: string }[] = [
  { id: "red", hex: "#EF4444", label: "Red" },
  { id: "blue", hex: "#3B82F6", label: "Blue" },
  { id: "green", hex: "#22C55E", label: "Green" },
  { id: "yellow", hex: "#EAB308", label: "Yellow" },
  { id: "purple", hex: "#A855F7", label: "Purple" },
  { id: "black", hex: "#171717", label: "Black" },
];

export function markupColorHex(color: MarkupColor): string {
  return MARKUP_COLORS.find((c) => c.id === color)?.hex ?? "#EF4444";
}

export const MARKUP_DRAG_MIME = "application/x-project-markup-component";

export type PlanFileKind = "pdf" | "image";

export const ACCEPTED_PLAN_FILE_ACCEPT =
  "application/pdf,image/jpeg,image/png,image/webp,.pdf,.jpg,.jpeg,.png,.webp";

export function getPlanFileKind(file: File): PlanFileKind | null {
  const type = file.type.toLowerCase();
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "";

  if (type === "application/pdf" || ext === "pdf") return "pdf";
  if (type === "image/jpeg" || ext === "jpg" || ext === "jpeg") return "image";
  if (type === "image/png" || ext === "png") return "image";
  if (type === "image/webp" || ext === "webp") return "image";

  return null;
}

/** Infer plan kind from a URL or filename when loading without a File object. */
export function getPlanKindFromName(fileNameOrUrl: string | null | undefined): PlanFileKind {
  const raw = String(fileNameOrUrl ?? "");
  const path = raw.split("?")[0] ?? raw;
  const ext = path.split(".").pop()?.toLowerCase() ?? "";
  if (ext === "pdf") return "pdf";
  if (ext === "jpg" || ext === "jpeg" || ext === "png" || ext === "webp") return "image";
  return "pdf";
}

export function isAcceptedPlanFile(file: File): boolean {
  return getPlanFileKind(file) !== null;
}

export function planFileTypeLabel(file: File): string {
  const kind = getPlanFileKind(file);
  if (!kind) return "Unknown";

  const type = file.type.toLowerCase();
  if (kind === "pdf") return "PDF";
  if (type === "image/jpeg") return "JPEG";
  if (type === "image/png") return "PNG";
  if (type === "image/webp") return "WEBP";

  const ext = file.name.split(".").pop()?.toLowerCase();
  if (ext === "jpg" || ext === "jpeg") return "JPEG";
  if (ext === "png") return "PNG";
  if (ext === "webp") return "WEBP";

  return "Image";
}

export type SandboxCatalogueItem = RouterOutputs["projectCatalogueSandbox"]["list"]["items"][number];

export function mapSandboxCatalogueToMarkupComponent(
  item: SandboxCatalogueItem,
  qty = 1,
): ProjectMarkupComponent {
  const unit = (item.dimensionUnit ?? "mm") as SandboxDimensionUnit;
  const displayName = resolveProductDisplayName(
    {
      manufacturer: item.manufacturer,
      model: item.productName,
      displayName: item.displayName,
    },
    { dedupeBrandPrefix: true },
  );
  const technical = formatProductBrandModelReference({
    manufacturer: item.manufacturer,
    model: item.productName,
  });
  return {
    id: `spc-${item.id}`,
    catalogueId: item.id,
    name: displayName,
    subtitle: technical !== displayName ? technical : null,
    category: item.category?.trim() || "Uncategorized",
    qty,
    imageUrl: item.imageUrl,
    dimensionsLabel: formatSandboxDimensions(item.width, item.height, item.depth, unit),
    weightLabel: formatSandboxWeight(item.weight, item.weightUnit),
  };
}

export function mapBomItemToMarkupComponent(item: {
  componentId: string;
  displayName: string;
  productName: string;
  manufacturer: string | null;
  quantitySold: number;
  imageUrl: string | null;
  dimensionsLabel: string | null;
  weightLabel: string | null;
  catalogueId: number | null;
}): ProjectMarkupComponent {
  const technical = formatProductBrandModelReference({
    manufacturer: item.manufacturer,
    model: item.productName,
  });
  return {
    id: item.componentId,
    catalogueId: item.catalogueId ?? 0,
    name: item.displayName || item.productName,
    subtitle: technical && technical !== (item.displayName || item.productName) ? technical : null,
    category: item.manufacturer?.trim() || "BOM",
    qty: item.quantitySold,
    imageUrl: item.imageUrl,
    dimensionsLabel: item.dimensionsLabel,
    weightLabel: item.weightLabel,
  };
}

export function createDefaultPlacedMarker(
  partial: Pick<PlacedMarker, "id" | "componentId" | "pageNumber" | "x" | "y" | "shape" | "color" | "calloutX" | "calloutY">,
): PlacedMarker {
  return {
    ...partial,
    markerScale: DEFAULT_MARKER_SCALE,
    calloutWidth: DEFAULT_CALLOUT_WIDTH,
    calloutHeight: DEFAULT_CALLOUT_HEIGHT,
    createdAt: new Date().toISOString(),
  };
}
